import {objectFromEntries} from '@benchling/js-utils';

import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {ObjectType} from 'app-core/framework/CoreObjectConstants';
import {getIdPrefixForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {assert} from 'Asserts';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import columnTypeToTypeDef from 'data-frames/util/columnTypeToTypeDef';
import ColumnTypeValidationException from 'data-frames/util/ColumnTypeValidationException';
import findColumn from 'data-frames/util/findColumn';
import getUserColumns from 'data-frames/util/getUserColumns';
import validateColumnType, {ColumnTypeValidationOptions} from 'data-frames/util/validateColumnType';
import isValidBenchlingTypeValue from 'data-types/util/isValidBenchlingTypeValue';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {JSONValue, assertNever} from 'util/Types';

import {PIPELINE_STEP_SHAPES} from './GeneratedPipelineStepShapes';
import {PipelineStepConfig} from './PipelineTypes';

export interface StepDataShape {
  kind: PipelineStepDataKind;
  isMulti: boolean;
  isRequired: boolean;
  systemName: string;
  displayName: string;
}

export type StepConfigShape = Record<string, PipelineConfigJsonSpec>;

export interface PipelineStepShape {
  stepType: PipelineStepType;
  isDeprecated: boolean;
  version: number;
  inputDataShapes: Record<string, StepDataShape>;
  outputDataShapes: Record<string, StepDataShape>;
  config: StepConfigShape | null;
}

export interface EnumOptions {
  value: string | number;
  displayName?: string;
  iconName?: BenchlingIconName;
  svgImagePath?: string;
}

// Keep in sync with `benchling/pipelines/step_type_library/manifests/types/config_specs.yaml` on the back-end
export type PipelineConfigJsonSpec = {required: boolean} & (
  | {kind: 'string'; value?: string}
  | {kind: 'number'}
  | {kind: 'integer'; minValue?: number; maxValue?: number}
  | {kind: 'boolean'}
  | {kind: 'enum'; choices: ReadonlyArray<string> | ReadonlyArray<number> | ReadonlyArray<EnumOptions>}
  | {kind: 'columnReference'; inputDataName: string; validationOptions: ColumnTypeValidationOptions | null}
  | {kind: 'columnValue'; columnSystemName: string; inputDataName: string; inputDataIndex: number}
  | {kind: 'tableReference'; inputDataName: string; inputDataIndex: number}
  | {kind: 'array'; elementSpec: PipelineConfigJsonSpec; minItems?: number; maxItems?: number}
  | {kind: 'shape'; fields: Record<string, PipelineConfigJsonSpec>}
  | {
      kind: 'taggedUnion';
      tagKey: string;
      choices: ReadonlyArray<Extract<PipelineConfigJsonSpec, {kind: 'shape'}>>;
    }
  | {kind: 'domain'; types: Array<ObjectType>}
  | {kind: 'anything'}
);

/**
 * Get the typescript shape of the given spec kind
 *
 * @example GetPipelineConfigSpecType<'enum'> -> {kind: 'enum'; choices: ReadonlyArray<string> | ReadonlyArray<number>}
 */
export type GetPipelineConfigSpecType<T extends PipelineConfigJsonSpec['kind']> = Extract<
  PipelineConfigJsonSpec,
  {kind: T}
>;

export function isStringSpec(
  spec: PipelineConfigJsonSpec
): spec is Extract<PipelineConfigJsonSpec, {kind: 'string'}> {
  return spec.kind === 'string';
}

/**
 * Return whether the input value is valid for the given Pipeline Config JSON spec.
 *
 * @example
 * ```
 * isValueValid('foo', {kind: 'string', required: true}, inputDataFrames) -> true
 * isValueValid(null, {kind: 'string', required: true}, inputDataFrames) -> false
 * isValueValid('hi', {kind: 'columnReference', ...}, dataFrameWithHiColumnSpec) -> true
 * ```
 *
 * @param inputDataFrames Map of input step data names to data frames' columns. See getInputStepDatasRecord.
 * @see examples in StepShapeUtils-test.ts
 */
export function isValueValid(
  value: JSONValue,
  spec: PipelineConfigJsonSpec,
  inputDataFrames: Record<string, Array<DataFrameSpec>>
): boolean {
  if (!spec.required && value == null) {
    return true;
  }
  switch (spec.kind) {
    case 'string':
      return (
        typeof value === 'string' && ((spec.value != null && value === spec.value) || spec.value == null)
      );
    case 'number':
      return typeof value === 'number';
    case 'integer':
      if (typeof value !== 'number') {
        return false;
      }
      if (spec.minValue != null && value < spec.minValue) {
        return false;
      }
      if (spec.maxValue != null && value > spec.maxValue) {
        return false;
      }
      return Number.isInteger(value);
    case 'boolean':
      return typeof value === 'boolean';
    case 'columnReference': {
      if (typeof value !== 'string') {
        return false;
      }
      try {
        const columns = (inputDataFrames[spec.inputDataName] ?? []).flatMap(({columns}) => columns);
        const column = findColumn({columns}, value);
        if (column != null && spec.validationOptions) {
          validateColumnType(column.type, spec.validationOptions);
        } else if (column == null) {
          return false;
        }
      } catch (err) {
        if (err instanceof ColumnTypeValidationException) {
          return false;
        }
        throw err;
      }
      return true;
    }
    case 'columnValue': {
      // Column values could have any type, and we don't have access to the underlying data, so don't
      // try to validate
      return true;
    }
    case 'tableReference': {
      if (!Array.isArray(value)) {
        return false;
      }
      if (value.length === 0) {
        return false;
      }
      if (!value.every((row) => typeof row === 'object' && row != null && !Array.isArray(row))) {
        return false;
      }

      const rows = value as Array<Record<string, JSONValue>>;

      const inputDataFrameSpecs = inputDataFrames[spec.inputDataName];
      if (inputDataFrameSpecs == null || inputDataFrameSpecs.length <= spec.inputDataIndex) {
        return false;
      }

      const columns = getUserColumns(inputDataFrameSpecs[spec.inputDataIndex]);

      return rows.every((row) =>
        columns.every(
          (column) =>
            row.hasOwnProperty(column.systemName) &&
            isValidBenchlingTypeValue(row[column.systemName], columnTypeToTypeDef(column.type))
        )
      );
    }
    case 'array': {
      if (!Array.isArray(value)) {
        return false;
      }
      if (spec.minItems != null && value.length < spec.minItems) {
        return false;
      }
      if (spec.maxItems != null && value.length > spec.maxItems) {
        return false;
      }
      return value.every((element) => isValueValid(element, spec.elementSpec, inputDataFrames));
    }
    case 'enum':
      return spec.choices.some((choice) => (typeof choice === 'object' ? choice.value : choice) === value);
    case 'taggedUnion': {
      if (typeof value !== 'object' || value == null || Array.isArray(value)) {
        return false;
      }
      const tagValue = value[spec.tagKey];
      if (tagValue == null) {
        return false;
      }
      const tagSpec = spec.choices.find((choice) => {
        const tagField = choice.fields[spec.tagKey];
        return tagField != null && tagField.kind === 'string' && tagField.value === tagValue;
      });
      if (tagSpec == null) {
        return false;
      }
      return isValueValid(value, tagSpec, inputDataFrames);
    }
    case 'shape':
      if (typeof value !== 'object' || value == null || Array.isArray(value)) {
        return false;
      }
      return Object.entries(spec.fields).every(([fieldName, fieldSpec]) => {
        const fieldValue = value[fieldName];
        return isValueValid(fieldValue, fieldSpec, inputDataFrames);
      });
    case 'domain':
      if (typeof value !== 'string') {
        return false;
      }
      return spec.types.some((type) =>
        new RegExp(`^${getIdPrefixForObjectType(type)}_[A-Za-z0-9]+$`).test(value)
      );
    case 'anything':
      return true;
    default:
      assert(false, `Unexpected jsonSpec ${assertNever(spec)}`);
  }
}

/**
 * Get a step type's config shape, which can be used with `isValueValid` to validate a `PipelineStepConfig`.
 */
export function getStepTypeConfigShape(
  stepType: PipelineStepType,
  stepVersion: number
): GetPipelineConfigSpecType<'shape'> {
  return {
    kind: 'shape',
    fields: PIPELINE_STEP_SHAPES[stepType][stepVersion].config ?? {},
    required: true,
  };
}

export function getPipelineStepConfig<StepType extends PipelineStepType, StepVersion extends number>(
  step: GetAnalysisPipelineStep,
  stepType: StepType,
  stepVersion: StepVersion
): PipelineStepConfig<StepType, StepVersion> {
  // TODO: BNCH-104744: remove step version bump temporarily backward compat code
  if (stepType === PipelineStepType.SAVE_TO_RESULTS && stepVersion === 2) {
    const config = getSaveToResultsStepV2Config(step);
    return config as PipelineStepConfig<StepType, StepVersion>;
  }
  assert(
    step.definition.stepType === stepType && step.definition.stepVersion === stepVersion,
    'Unexpected step type and version'
  );
  assert(step.config != null, 'Expected non-null step config.');

  return objectFromEntries(
    step.config.variables.map((variable) => [variable.name, variable.value])
  ) as PipelineStepConfig<StepType, StepVersion>;
}

/**
 * This is a stop-gap function aimed to upgrade existing save_to_results v1 config to v2 config before we have the backfil script
 */
function getSaveToResultsStepV2Config(step: GetAnalysisPipelineStep): Record<string, unknown> {
  assert(step.config != null, 'Expected non-null step config.');
  assert(
    step.definition.stepType === PipelineStepType.SAVE_TO_RESULTS,
    `Expecting ${PipelineStepType.SAVE_TO_RESULTS}`
  );
  assert(
    step.definition.stepVersion === 1 || step.definition.stepVersion === 2,
    `Expecting step version of ${PipelineStepType.SAVE_TO_RESULTS} to be 1 or 2`
  );
  if (step.definition.stepVersion === 1) {
    const configV1 = objectFromEntries(
      step.config.variables.map((variable) => [variable.name, variable.value])
    );
    const configV2 = {
      ...configV1,
      targetSpecificMapping: null,
    };
    return configV2;
  } else if (step.definition.stepVersion === 2) {
    return objectFromEntries(step.config.variables.map((variable) => [variable.name, variable.value]));
  }

  // oxlint-disable-next-line no-unused-expressions
  step.definition.stepVersion satisfies never;
  throw new Error('Shpuld never reach here.');
}
